/**
 * Email Sync Analytics Component
 * 
 * Shows comprehensive analytics about email sync:
 * - Import summary (bookings, reservations, activities, etc.)
 * - Time saved calculation
 * - Coverage percentage
 * - Missing items suggestions
 */

import { useState, useEffect } from 'react';
import { View, ScrollView } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, SubHeading, Body, Caption } from '@/components/ui/Text';
import { subscribeToTripBookings, type Booking } from '@/services/bookings';
import { getSyncMetadata, getSyncHistory, type SyncHistoryEntry } from '@/services/emailSyncHistory';
import type { InboxProviderId } from '@/services/inboxSync';
import { formatDistanceToNow } from 'date-fns';

type EmailSyncAnalyticsProps = {
  tripId: string;
  provider: InboxProviderId;
};

// Estimated time to manually enter each type of booking (in minutes)
const MANUAL_ENTRY_TIME: Record<string, number> = {
  flight: 3,
  hotel: 2,
  car: 2,
  restaurant: 1,
  activity: 2,
  other: 2,
};

export function EmailSyncAnalytics({ tripId, provider }: EmailSyncAnalyticsProps) {
  const theme = useTheme();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [syncMetadata, setSyncMetadata] = useState<{
    lastSyncAt: number | null;
    lastSyncSuccess: boolean;
    totalBookingsFound: number;
    syncCount: number;
  } | null>(null);
  const [syncHistory, setSyncHistory] = useState<SyncHistoryEntry[]>([]);

  useEffect(() => {
    if (!tripId) return;

    // Subscribe to bookings
    const unsubscribe = subscribeToTripBookings(tripId, (updatedBookings) => {
      // Filter bookings by provider
      const providerBookings = updatedBookings.filter(
        (booking) => booking.emailProvider === provider
      );
      setBookings(providerBookings);
    });

    // Load sync metadata and history
    loadSyncData();

    return unsubscribe;
  }, [tripId, provider]);

  const loadSyncData = async () => {
    try {
      const [metadata, history] = await Promise.all([
        getSyncMetadata(tripId, provider),
        getSyncHistory(tripId, provider),
      ]);
      setSyncMetadata(metadata);
      setSyncHistory(history);
    } catch (error) {
      console.error('[EmailSyncAnalytics] Error loading sync data:', error);
    }
  };

  // Calculate analytics
  const analytics = calculateAnalytics(bookings, syncMetadata, syncHistory);

  return (
    <ScrollView
      style={{ flex: 1 }}
      contentContainerStyle={{ padding: theme.spacing(2), gap: theme.spacing(2) }}
    >
      {/* Import Summary */}
      <Card variant="outlined">
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
          <Ionicons name="stats-chart" size={24} color={theme.colors.primary} />
          <SubHeading>Import Summary</SubHeading>
        </View>
        <View style={{ gap: theme.spacing(1.5) }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Total Bookings</Body>
            <ThemedText variant="bodyStrong">{analytics.totalBookings}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Flights</Body>
            <ThemedText variant="bodyStrong">{analytics.byType.flight}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Hotels</Body>
            <ThemedText variant="bodyStrong">{analytics.byType.hotel}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Car Rentals</Body>
            <ThemedText variant="bodyStrong">{analytics.byType.car}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Restaurants</Body>
            <ThemedText variant="bodyStrong">{analytics.byType.restaurant}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Activities</Body>
            <ThemedText variant="bodyStrong">{analytics.byType.activity}</ThemedText>
          </View>
        </View>
      </Card>

      {/* Time Saved */}
      <Card variant="filled" style={{ backgroundColor: theme.colors.primaryContainer }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
          <Ionicons name="time-outline" size={24} color={theme.colors.primary} />
          <SubHeading color={theme.colors.onPrimaryContainer}>Time Saved</SubHeading>
        </View>
        <View style={{ alignItems: 'center' }}>
          <ThemedText variant="h1" color={theme.colors.onPrimaryContainer}>
            {analytics.timeSavedMinutes}
          </ThemedText>
          <Body color={theme.colors.onPrimaryContainer}>
            minute{analytics.timeSavedMinutes !== 1 ? 's' : ''} saved
          </Body>
          <Caption color={theme.colors.onPrimaryContainer} style={{ marginTop: theme.spacing(0.5) }}>
            vs. manual entry
          </Caption>
        </View>
      </Card>

      {/* Sync Statistics */}
      <Card variant="outlined">
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
          <Ionicons name="sync-outline" size={24} color={theme.colors.primary} />
          <SubHeading>Sync Statistics</SubHeading>
        </View>
        <View style={{ gap: theme.spacing(1.5) }}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Total Syncs</Body>
            <ThemedText variant="bodyStrong">{analytics.syncCount}</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Last Sync</Body>
            <ThemedText variant="bodyStrong">
              {analytics.lastSyncAt
                ? formatDistanceToNow(new Date(analytics.lastSyncAt), { addSuffix: true })
                : 'Never'}
            </ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Success Rate</Body>
            <ThemedText variant="bodyStrong">{analytics.successRate}%</ThemedText>
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <Body>Average per Sync</Body>
            <ThemedText variant="bodyStrong">
              {analytics.averageBookingsPerSync.toFixed(1)} bookings
            </ThemedText>
          </View>
        </View>
      </Card>

      {/* Coverage Analysis */}
      {analytics.coveragePercentage > 0 && (
        <Card variant="outlined">
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
            <Ionicons name="checkmark-circle-outline" size={24} color={theme.colors.primary} />
            <SubHeading>Coverage</SubHeading>
          </View>
          <View style={{ marginBottom: theme.spacing(1.5) }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: theme.spacing(0.5) }}>
              <Body>Email Coverage</Body>
              <ThemedText variant="bodyStrong">{analytics.coveragePercentage}%</ThemedText>
            </View>
            <View
              style={{
                height: 8,
                backgroundColor: theme.colors.background,
                borderRadius: 4,
                overflow: 'hidden',
                marginTop: theme.spacing(1),
              }}
            >
              <View
                style={{
                  height: '100%',
                  width: `${analytics.coveragePercentage}%`,
                  backgroundColor: theme.colors.primary,
                }}
              />
            </View>
          </View>
          <Caption color={theme.colors.textSecondary}>
            {analytics.totalBookings} booking{analytics.totalBookings !== 1 ? 's' : ''} imported from
            email
          </Caption>
        </Card>
      )}

      {/* Suggestions */}
      {analytics.suggestions.length > 0 && (
        <Card variant="outlined">
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1.5), marginBottom: theme.spacing(2) }}>
            <Ionicons name="bulb-outline" size={24} color={theme.colors.primary} />
            <SubHeading>Suggestions</SubHeading>
          </View>
          <View style={{ gap: theme.spacing(1) }}>
            {analytics.suggestions.map((suggestion, index) => (
              <View
                key={index}
                style={{
                  flexDirection: 'row',
                  gap: theme.spacing(1),
                  padding: theme.spacing(1),
                  backgroundColor: theme.colors.background,
                  borderRadius: theme.radii.md,
                }}
              >
                <Ionicons name="information-circle-outline" size={16} color={theme.colors.primary} />
                <Body style={{ flex: 1, fontSize: 13 }}>{suggestion}</Body>
              </View>
            ))}
          </View>
        </Card>
      )}
    </ScrollView>
  );
}

function calculateAnalytics(
  bookings: Booking[],
  syncMetadata: {
    lastSyncAt: number | null;
    lastSyncSuccess: boolean;
    totalBookingsFound: number;
    syncCount: number;
  } | null,
  syncHistory: SyncHistoryEntry[]
) {
  // Count by type
  const byType = {
    flight: bookings.filter((b) => b.type === 'flight').length,
    hotel: bookings.filter((b) => b.type === 'hotel').length,
    car: bookings.filter((b) => b.type === 'car').length,
    restaurant: bookings.filter((b) => b.type === 'restaurant').length,
    activity: bookings.filter((b) => b.type === 'activity').length,
    other: bookings.filter((b) => b.type === 'other').length,
  };

  // Calculate time saved
  const timeSavedMinutes = bookings.reduce((total, booking) => {
    const entryTime = MANUAL_ENTRY_TIME[booking.type] || MANUAL_ENTRY_TIME.other;
    return total + entryTime;
  }, 0);

  // Sync statistics
  const syncCount = syncMetadata?.syncCount || 0;
  const successfulSyncs = syncHistory.filter((entry) => entry.success).length;
  const successRate = syncCount > 0 ? Math.round((successfulSyncs / syncCount) * 100) : 0;
  const averageBookingsPerSync =
    syncCount > 0 ? (syncMetadata?.totalBookingsFound || 0) / syncCount : 0;

  // Coverage (simplified - assumes 10 bookings is 100% coverage for a typical trip)
  const estimatedTotalBookings = 10; // This could be calculated based on trip duration, etc.
  const coveragePercentage = Math.min(
    Math.round((bookings.length / estimatedTotalBookings) * 100),
    100
  );

  // Generate suggestions
  const suggestions: string[] = [];
  if (bookings.length === 0) {
    suggestions.push('No bookings imported yet. Try syncing your email to get started.');
  } else if (byType.flight === 0) {
    suggestions.push('No flights found. Make sure flight confirmation emails are in your inbox.');
  }
  if (byType.hotel === 0 && bookings.length > 0) {
    suggestions.push('No hotels found. Check if hotel confirmation emails are being received.');
  }
  if (syncCount === 0) {
    suggestions.push('Sync your email regularly to keep bookings up to date.');
  } else if (syncMetadata?.lastSyncAt) {
    const daysSinceLastSync = Math.floor(
      (Date.now() - syncMetadata.lastSyncAt) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceLastSync > 7) {
      suggestions.push(`Last sync was ${daysSinceLastSync} days ago. Consider syncing again.`);
    }
  }
  if (coveragePercentage < 50 && bookings.length > 0) {
    suggestions.push('Low coverage detected. You may have bookings in other email accounts.');
  }

  return {
    totalBookings: bookings.length,
    byType,
    timeSavedMinutes,
    syncCount,
    lastSyncAt: syncMetadata?.lastSyncAt ?? null,
    successRate,
    averageBookingsPerSync,
    coveragePercentage,
    suggestions,
  };
}

